import type React from "react";
import {useCallback, useRef} from "react";

import useMyCamera from "~/contexts/camera/useMyCamera";
import {useGlobalEvent} from "~/contexts/event/GlobalEventBusContext";

/**
 * A hook that encapsulates zoom-related behavior.
 * It registers global event handlers for touch (pinch) and wheel (scroll-to-zoom)
 * interactions that modify targetZoom.current.
 */
const useZoomControls = () => {
  const {targetZoom} = useMyCamera();

  // Ref to capture the initial state for pinch gestures.
  const initialTouchDistanceRef = useRef<number | null>(null);
  const initialZoomRef = useRef<number>(0);

  // When two touches start, record the initial distance and zoom.
  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (event.touches.length === 2) {
        const [touch1, touch2] = Array.from(event.touches);
        initialTouchDistanceRef.current = Math.hypot(
          touch1!.clientX - touch2!.clientX,
          touch1!.clientY - touch2!.clientY,
        );
        initialZoomRef.current = targetZoom.current;
      }
    },
    [targetZoom],
  );

  // When touches move, update the zoom based on the change in distance.
  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (
        event.touches.length === 2 &&
        initialTouchDistanceRef.current !== null
      ) {
        const [touch1, touch2] = Array.from(event.touches);
        const currentDistance = Math.hypot(
          touch1!.clientX - touch2!.clientX,
          touch1!.clientY - touch2!.clientY,
        );
        const ratio = currentDistance / initialTouchDistanceRef.current;
        targetZoom.current = Math.max(0.1, initialZoomRef.current * ratio);
      }
    },
    [targetZoom],
  );

  // Clear the initial touch distance once fewer than two touches remain.
  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (event.touches.length < 2) {
        initialTouchDistanceRef.current = null;
      }
    },
    [],
  );

  // Use the wheel to smoothly adjust zoom.
  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      const logZoom = Math.log(targetZoom.current);
      const zoomDelta = event.deltaY * -0.002;
      targetZoom.current = Math.max(0.1, Math.exp(logZoom + zoomDelta));
    },
    [targetZoom],
  );

  // Register the zoom-related event handlers via global events.
  useGlobalEvent("touchstart", handleTouchStart);
  useGlobalEvent("touchmove", handleTouchMove);
  useGlobalEvent("touchend", handleTouchEnd);
  useGlobalEvent("wheel", handleWheel);

  // This hook is used for the side effects, so nothing needs to be returned.
  return null;
};

export default useZoomControls;
